import { useState } from "react";
import { BUSINESS, SERVICES } from "#/lib/constants";
import { sendQuoteEmailAction } from "#/lib/send-email";
import { Phone, Mail, Clock, Loader2, Check, ArrowRight, ChevronDown } from "lucide-react";

/**
 * EstimateForm — the estimate form as a light construction work-order / spec
 * sheet: one white bordered panel divided by hard hairline rules, fields in a
 * rigid grid, a single heavy flame action bar, plus the "Prefer to talk?"
 * contact strip beneath. Light on purpose: customer-facing forms are never dark
 * on this site (see memory/lessons.md 2026-07-25). Fully rectangular (zero
 * radius). Extracted from home `FinalCTA` so `/quote` can reuse it. No props.
 */
export function EstimateForm() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const form = e.currentTarget;
    const val = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)
        .value;
    const data = {
      name: val("name"),
      phone: val("phone"),
      email: val("email"),
      service: val("service"),
      propertyType: val("propertyType") || undefined,
      address: val("address") || undefined,
      message: val("message") || undefined,
      _hp: honeypot,
    };
    try {
      const result = await sendQuoteEmailAction({ data });
      if (result.success) setSubmitted(true);
      else setError(result.error || "Something went wrong. Please try again.");
    } catch {
      setError(`An error occurred. Please call us at ${BUSINESS.phone}.`);
    } finally {
      setIsSubmitting(false);
    }
  }

  return submitted ? (
    <SuccessPanel phone={BUSINESS.phone} />
  ) : (
    <>
      <form onSubmit={onSubmit} className="border border-graphite/15 bg-white shadow-sm">
        {/* Honeypot — hidden from users, catches bots */}
        <input
          type="text"
          name="_hp"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        {/* Header row */}
        <div className="border-b border-graphite/10 px-6 py-6 sm:px-8 sm:py-8">
          {/* Both labels stay on one line at every width. The tracking is
              tightened on small screens so they fit side by side instead of
              each wrapping mid-phrase. The "01" step number is gone: this form
              is not part of a numbered sequence. */}
          <div className="flex items-center justify-between gap-3 text-[0.65rem] font-bold uppercase tracking-[0.1em] sm:text-[0.7rem] sm:tracking-[0.2em]">
            <span className="whitespace-nowrap text-flame">Get an estimate</span>
            <span className="whitespace-nowrap text-concrete-dark">Free · No obligation</span>
          </div>
          <h2 className="font-heading mt-3 text-3xl font-black uppercase leading-[0.95] tracking-tight text-graphite sm:text-4xl lg:text-5xl">
            Let&apos;s build it right
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-concrete-dark">
            Tell us about your project and we&apos;ll get you a free, no-pressure estimate,
            usually on-site within the week. No deposit, no obligation.
          </p>
        </div>

        {/* Name / Phone / Email */}
        <div className="grid divide-y divide-graphite/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <Cell label="Name" htmlFor="name" required>
            <input id="name" name="name" type="text" required autoComplete="name" placeholder="John Smith" className={inputCls} />
          </Cell>
          <Cell label="Phone" htmlFor="phone" required>
            <input id="phone" name="phone" type="tel" required inputMode="tel" autoComplete="tel" placeholder="(302) 555-0123" className={inputCls} />
          </Cell>
          <Cell label="Email" htmlFor="email" required>
            <input id="email" name="email" type="email" required inputMode="email" autoComplete="email" placeholder="john@example.com" className={inputCls} />
          </Cell>
        </div>

        {/* Service / Property type */}
        <div className="grid divide-y divide-graphite/10 border-t border-graphite/10 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
          <Cell label="Service needed" htmlFor="service" required>
            <SelectField id="service" required defaultValue="">
              <option value="" disabled hidden>
                Select a service
              </option>
              {SERVICES.map((s) => (
                <option key={s.slug} value={s.title} className="text-graphite">
                  {s.title}
                </option>
              ))}
            </SelectField>
          </Cell>
          <Cell label="Property type" htmlFor="propertyType">
            <SelectField id="propertyType" defaultValue="">
              <option value="" disabled hidden>
                Select type
              </option>
              <option value="Residential" className="text-graphite">Residential</option>
              <option value="Commercial" className="text-graphite">Commercial</option>
            </SelectField>
          </Cell>
        </div>

        {/* Job site address */}
        <Cell label="Job site address" htmlFor="address" optional className="border-t border-graphite/10">
          <input id="address" name="address" type="text" autoComplete="street-address" placeholder="123 Main St, Wilmington, DE" className={inputCls} />
        </Cell>

        {/* Project details */}
        <Cell label="Project details" htmlFor="message" optional className="border-t border-graphite/10">
          <textarea id="message" name="message" rows={3} autoComplete="off" placeholder="Slab size, timeline, finish, anything that helps us quote it." className={`${inputCls} resize-none`} />
        </Cell>

        {error && (
          <div role="alert" className="border-t border-red-300 bg-red-50 px-6 py-3 text-sm text-red-700 sm:px-8">
            {error}
          </div>
        )}

        {/* Action bar */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="group flex w-full items-center justify-between gap-4 bg-flame px-6 py-5 font-heading text-base font-bold uppercase tracking-wide text-white transition-colors hover:bg-flame-dark disabled:opacity-70 sm:px-8 sm:text-lg"
        >
          <span className="flex items-center gap-3">
            {isSubmitting && <Loader2 className="h-5 w-5 animate-spin" />}
            {isSubmitting ? "Sending…" : "Book my estimate"}
          </span>
          <ArrowRight className="h-6 w-6 transition-transform group-hover:translate-x-1" />
        </button>
      </form>

      {/* Contact strip beneath the document */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-concrete-dark">
        <span className="font-semibold text-graphite">Prefer to talk?</span>
        <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-2 text-graphite no-underline transition-colors hover:text-flame">
          <Phone className="h-4 w-4 text-flame" /> {BUSINESS.phone}
        </a>
        <a href={`mailto:${BUSINESS.email}`} className="inline-flex items-center gap-2 text-graphite no-underline transition-colors hover:text-flame">
          <Mail className="h-4 w-4 text-flame" /> {BUSINESS.email}
        </a>
        <span className="inline-flex items-center gap-2">
          <Clock className="h-4 w-4 text-flame" /> 24-hour response
        </span>
      </div>
    </>
  );
}

const inputCls =
  "min-h-11 w-full border-b-2 border-gray-200 bg-transparent py-1.5 text-base text-graphite outline-none transition-colors placeholder:text-concrete focus:border-flame";

function Cell({
  label,
  htmlFor,
  required,
  optional,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  optional?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`px-6 py-5 transition-colors focus-within:bg-gray-50 sm:px-8 sm:py-6 ${className ?? ""}`}>
      <label htmlFor={htmlFor} className="mb-2 block text-[0.7rem] font-bold uppercase tracking-[0.15em] text-graphite">
        {label}
        {required && <span className="text-flame"> *</span>}
        {optional && (
          <span className="font-medium normal-case tracking-normal text-concrete"> (optional)</span>
        )}
      </label>
      {children}
    </div>
  );
}

function SelectField({
  id,
  required,
  defaultValue,
  children,
}: {
  id: string;
  required?: boolean;
  defaultValue: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <select
        id={id}
        name={id}
        required={required}
        defaultValue={defaultValue}
        className={`${inputCls} appearance-none pr-8 text-concrete valid:text-graphite`}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-5 w-5 -translate-y-1/2 text-concrete" />
    </div>
  );
}

function SuccessPanel({ phone }: { phone: string }) {
  return (
    <div className="border border-flame/30 bg-white px-6 py-12 text-center shadow-sm sm:px-12">
      <div className="mx-auto flex h-14 w-14 items-center justify-center bg-flame text-white">
        <Check className="h-8 w-8" strokeWidth={3} />
      </div>
      <h2 className="font-heading mt-5 text-2xl font-black uppercase tracking-tight text-graphite sm:text-3xl">
        Request received
      </h2>
      <p className="mx-auto mt-3 max-w-md text-concrete-dark">
        Thanks. We&apos;ll be in touch within 24 hours to schedule your free estimate. Need it
        sooner? Call us at {phone}.
      </p>
    </div>
  );
}
